import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import { UiSuspensefulOutlet } from '@experiences/ui-common';
import type { PropsWithChildren } from 'react';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { routePaths } from '../../common/constants/routePaths';
import { useOrganizationName } from '../../common/hooks/useOrganizationName';
import BreadcrumbProvider from '../../common/providers/BreadcrumbProvider';
import CheckGuard from '../../container/helpers/CheckGuard';
import type { UiRouteObject } from '../../container/routeConfigs/UiRouteObject';
import GovernanceCreateComponent from './GovernanceCreateComponent';

const GovernancePageComponent = React.lazy(() => import('./GovernancePageComponent'));

const GovernanceProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const { formatMessage: translate } = useIntl();

    const EnableDataAccessPoliciesGovernance = useFeatureFlagValue(Features.EnableDataAccessPoliciesGovernance.name);

    const organizationName = useOrganizationName();

    const breadcrumbs = useMemo(() => [
        {
            index: 0,
            link: routePaths.admin,
            name: organizationName,
        },
        {
            index: 1,
            link: routePaths.adminGovernance,
            name: translate({ id: 'CLIENT_GOVERNANCE' }),
        },
        {
            index: 2,
            link: routePaths.adminGovernanceCreate,
            name: translate({ id: 'CLIENT_GOVERNANCE_CREATE_POLICY' }),
        },
    ], [ organizationName, translate ]);

    return <BreadcrumbProvider breadcrumbs={breadcrumbs}>
        {CheckGuard(EnableDataAccessPoliciesGovernance, <>
            {children}
        </>)}
    </BreadcrumbProvider>;
};

export const GovernanceRoutes: UiRouteObject = {
    path: routePaths.adminGovernance,
    element: <GovernanceProvider>
        <UiSuspensefulOutlet />
    </GovernanceProvider>,
    children: [
        {
            index: true,
            element: <GovernancePageComponent />,
        },
        {
            path: routePaths.adminGovernanceCreate,
            element: <GovernanceCreateComponent />,
        },
    ],
};
